import React, { useEffect, useState } from "react";
import Header from "../../../components/Header/Header";
import Tabs from "../../../components/Tabs/Tabs";
import mes_tab_data from "../../../assets/mockedData/mes_tab_data";
import { savePathToLocalStorage } from "../../../utils/helpers";
import mesApi from "../../../Api/mes.api";
import { Trial, TrialSchedule, TrialIngredient } from "../../../types/mes";
import { ToastContainer, toast } from "react-toastify";
import { printWorkOrder, printBulkWorkOrders } from "./trialUtils";
import { t, dateLocale } from "../../../i18n/locale";

interface ScheduleEntry extends TrialSchedule {
  trial?: Trial;
  product_name?: string;
}

const DAY_NAMES = ["Ponedjeljak", "Utorak", "Srijeda", "Cetvrtak", "Petak", "Subota", "Nedjelja"];
const STATUS_ICON: Record<string, string> = {
  planned: "📋",
  done: "✅",
  cancelled: "❌",
  draft: "📝",
  confirmed: "✔️",
  promoted: "🚀",
};

/**
 * Trial Dashboard - Weekly schedule view for trials
 */
const TrialDashboard = () => {
  savePathToLocalStorage();

  const [weekOffset, setWeekOffset] = useState(0);
  const [schedule, setSchedule] = useState<ScheduleEntry[]>([]);
  const [draftTrials, setDraftTrials] = useState<Trial[]>([]);
  const [availableTrials, setAvailableTrials] = useState<Trial[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDayIdx, setSelectedDayIdx] = useState<number | null>(null);
  const [selectedTrialId, setSelectedTrialId] = useState<number | null>(null);
  const [plannedOrder, setPlannedOrder] = useState(1);

  const today = new Date();
  const getWeekStart = (date: Date, offset: number) => {
    const d = new Date(date);
    d.setDate(d.getDate() - d.getDay() + 1 + offset * 7); // Monday
    return d;
  };

  const weekStart = getWeekStart(today, weekOffset);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);

  useEffect(() => {
    loadData();
  }, [weekOffset]);

  const loadData = async () => {
    setLoading(true);
    try {
      const fromDate = weekStart.toISOString().split("T")[0];
      const toDate = weekEnd.toISOString().split("T")[0];

      const [scheduleData, trialsData] = await Promise.all([
        mesApi.getTrialSchedule({ fromDate, toDate }),
        mesApi.getTrials({}),
      ]);

      // Enhance schedule with trial details
      const enhancedSchedule: ScheduleEntry[] = scheduleData.map((s) => ({
        ...s,
        trial: s.Trial_TrialToTrialSchedule || trialsData.find((t) => t.ID === s.Trial),
        product_name: s.Trial_TrialToTrialSchedule?.Product_ProductToTrial?.ProductName,
      }));

      setSchedule(enhancedSchedule);
      setDraftTrials(trialsData.filter((t) => t.Status === "draft"));
      setAvailableTrials(trialsData.filter((t) => t.Status === "draft" || t.Status === "confirmed"));
    } catch (err: any) {
      toast.error(t("Greska pri ucitavanju: {message}", { message: err.message }));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString(dateLocale(), { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const getDayDate = (dayIdx: number) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + dayIdx);
    return d;
  };

  const getScheduleForDay = (dayIdx: number) => {
    const dayDate = getDayDate(dayIdx).toISOString().split("T")[0];
    return schedule.filter((s) => {
      const scheduleDate = new Date(s.PlannedDate).toISOString().split("T")[0];
      return scheduleDate === dayDate;
    }).sort((a, b) => a.PlannedOrder - b.PlannedOrder);
  };

  const handleStatusChange = async (scheduleId: number, newStatus: string) => {
    try {
      await mesApi.updateTrialSchedule(scheduleId, { Status: newStatus });
      toast.success(t("Status azuriran"));
      loadData();
    } catch (err: any) {
      toast.error(t("Greska: {message}", { message: err.message }));
    }
  };

  const handleDeleteSchedule = async (scheduleId: number) => {
    if (!window.confirm(t("Jeste li sigurni da zelite ukloniti ovu probu iz plana?"))) return;
    try {
      await mesApi.updateTrialSchedule(scheduleId, { Status: "cancelled" });
      toast.success(t("Proba uklonjena iz plana"));
      loadData();
    } catch (err: any) {
      toast.error(t("Greska: {message}", { message: err.message }));
    }
  };

  const handleAddToSchedule = async () => {
    if (selectedDayIdx === null || !selectedTrialId) return;

    const dayDate = getDayDate(selectedDayIdx).toISOString().split("T")[0];

    try {
      // Check if already scheduled for this day
      const existing = schedule.find(
        (s) => s.Trial === selectedTrialId &&
               new Date(s.PlannedDate).toISOString().split("T")[0] === dayDate
      );

      if (existing) {
        toast.warning(t("Ova proba je vec dodana za taj dan"));
        return;
      }

      // Create new schedule entry via API
      // Note: This would need a createTrialSchedule endpoint
      toast.info(t("Funkcionalnost dodavanja u plan - potreban backend endpoint"));

      setSelectedDayIdx(null);
      setSelectedTrialId(null);
      setPlannedOrder(1);
    } catch (err: any) {
      toast.error(t("Greska: {message}", { message: err.message }));
    }
  };

  const handlePrintWorkOrder = async (trial: Trial) => {
    try {
      const ingredients = await mesApi.getTrialIngredients(trial.ID);
      if (!printWorkOrder(trial, ingredients)) {
        toast.error(t("Nije moguce otvoriti prozor za ispis"));
      }
    } catch (err: any) {
      toast.error(t("Greska pri ispisu: {message}", { message: err.message }));
    }
  };

  const handleBulkPrint = async (dayIdx: number) => {
    const daySchedule = getScheduleForDay(dayIdx);
    const trialsWithIngredients: { trial: Trial; ingredients: TrialIngredient[] }[] = [];

    for (const entry of daySchedule) {
      if (entry.trial && entry.Status !== "cancelled") {
        try {
          const ingredients = await mesApi.getTrialIngredients(entry.trial.ID);
          trialsWithIngredients.push({ trial: entry.trial, ingredients });
        } catch (err) {
          console.error(`Failed to load ingredients for trial ${entry.trial.TrialCode}`);
        }
      }
    }

    if (trialsWithIngredients.length === 0) {
      toast.warning(t("Nema proba za ispis tog dana"));
      return;
    }

    if (!printBulkWorkOrders(trialsWithIngredients, getDayDate(dayIdx))) {
      toast.error(t("Nije moguce otvoriti prozor za ispis"));
    }
  };

  return (
    <div className="mes-page">
      <Header title={t("MES - Dashboard proba")} />
      <div className="main-content-flex">
        <Tabs data={mes_tab_data} />
        <div className="section mes-content">
          {/* Draft Trials */}
          {draftTrials.length > 0 && (
            <div className="mes-section">
              <h3>📝 {t("Draft probe ({count})", { count: draftTrials.length })}</h3>
              <p style={{ color: "#adb3bd", marginBottom: "1rem" }}>
                {t("Draft probe - jos nisu izvedene. Mogu se rasporediti u plan i ispisati nalog.")}
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                {draftTrials.slice(0, 5).map((trial) => (
                  <div
                    key={trial.ID}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "0.75rem",
                      background: "rgba(255,255,255,0.02)",
                      borderRadius: "4px",
                      border: "1px solid rgba(255,255,255,0.05)",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <strong>{trial.TrialCode}</strong>
                      <span style={{ marginLeft: "1rem", color: "#adb3bd" }}>
                        {trial.Product_ProductToTrial?.ProductName || "-"}
                        {trial.Purpose && ` - ${trial.Purpose}`}
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      <span style={{ color: "#adb3bd" }}>
                        {trial.DoughMassKg != null ? Number(trial.DoughMassKg).toFixed(1) : "-"} kg
                      </span>
                      <button
                        className="btn-primary"
                        style={{ padding: "0.25rem 0.5rem", fontSize: "0.85rem" }}
                        onClick={() => handlePrintWorkOrder(trial)}
                      >
                        🖨️ {t("Nalog")}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Week Navigation */}
          <div className="mes-section">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <button
                  className="btn-primary"
                  onClick={() => setWeekOffset(weekOffset - 1)}
                >
                  {t("◀ Prethodni")}
                </button>
                <button
                  className="btn-primary"
                  onClick={() => setWeekOffset(weekOffset + 1)}
                >
                  {t("Sljedeci ▶")}
                </button>
                <button
                  className="btn-primary"
                  onClick={() => setWeekOffset(0)}
                >
                  {t("Danas")}
                </button>
              </div>
              <h3 style={{ margin: 0 }}>
                {t("Tjedan: {from} - {to}", { from: formatDate(weekStart), to: formatDate(weekEnd) })}
              </h3>
            </div>
          </div>

          {/* Weekly Schedule */}
          {loading ? (
            <div className="mes-section">
              <p>{t("Ucitavanje...")}</p>
            </div>
          ) : (
            <div className="mes-section">
              {Array.from({ length: 7 }).map((_, dayIdx) => {
                const dayDate = getDayDate(dayIdx);
                const daySchedule = getScheduleForDay(dayIdx);
                const isToday = dayDate.toDateString() === today.toDateString();

                return (
                  <div
                    key={dayIdx}
                    style={{
                      marginBottom: "1rem",
                      padding: "1rem",
                      background: isToday ? "rgba(75, 153, 135, 0.1)" : "rgba(255,255,255,0.02)",
                      borderRadius: "8px",
                      border: isToday ? "1px solid #4b9987" : "1px solid rgba(255,255,255,0.05)",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                      <h4 style={{ margin: 0 }}>
                        {isToday && "📍 "}
                        {t(DAY_NAMES[dayIdx])} - {formatDate(dayDate)}
                        {daySchedule.length > 0 && ` (${t("{count} proba", { count: daySchedule.length })})`}
                      </h4>
                      {daySchedule.length > 0 && (
                        <button
                          className="btn-primary"
                          style={{ padding: "0.25rem 0.75rem", fontSize: "0.85rem" }}
                          onClick={() => handleBulkPrint(dayIdx)}
                          title={t("Ispisi sve naloge za ovaj dan")}
                        >
                          🖨️ {t("Ispisi sve ({count})", { count: daySchedule.filter(s => s.Status !== "cancelled").length })}
                        </button>
                      )}
                    </div>

                    {daySchedule.length === 0 ? (
                      <p style={{ color: "#adb3bd", margin: "0.5rem 0" }}>{t("Nema planiranih proba.")}</p>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                        {daySchedule.map((entry) => (
                          <div
                            key={entry.ID}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              padding: "0.5rem 0.75rem",
                              background: "rgba(0,0,0,0.2)",
                              borderRadius: "4px",
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <strong>{entry.PlannedOrder}.</strong>{" "}
                              {entry.trial?.TrialCode || "-"}
                              <span style={{ marginLeft: "0.75rem", color: "#adb3bd" }}>
                                {entry.product_name || entry.trial?.Product_ProductToTrial?.ProductName || "-"}
                                {entry.trial?.Purpose && ` - ${entry.trial.Purpose}`}
                              </span>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                              <select
                                value={entry.Status}
                                onChange={(e) => handleStatusChange(entry.ID, e.target.value)}
                                className="input-classic"
                                style={{ padding: "0.25rem 0.5rem", fontSize: "0.85rem" }}
                              >
                                <option value="planned">📋 {t("Planirano")}</option>
                                <option value="done">✅ {t("Gotovo")}</option>
                                <option value="cancelled">❌ {t("Otkazano")}</option>
                              </select>
                              {entry.trial && (
                                <button
                                  className="btn-primary"
                                  style={{ padding: "0.25rem 0.5rem", fontSize: "0.85rem" }}
                                  onClick={() => handlePrintWorkOrder(entry.trial!)}
                                >
                                  🖨️
                                </button>
                              )}
                              <button
                                className="btn-icon-danger"
                                style={{ padding: "0.25rem 0.5rem" }}
                                onClick={() => handleDeleteSchedule(entry.ID)}
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add trial to this day */}
                    <div style={{ marginTop: "0.75rem", display: "flex", gap: "0.5rem", alignItems: "center" }}>
                      <select
                        className="input-classic"
                        style={{ flex: 2 }}
                        value={selectedDayIdx === dayIdx ? (selectedTrialId || "") : ""}
                        onChange={(e) => {
                          setSelectedDayIdx(dayIdx);
                          setSelectedTrialId(e.target.value ? parseInt(e.target.value) : null);
                        }}
                      >
                        <option value="">{t("Dodaj probu...")}</option>
                        {availableTrials.map((trial) => (
                          <option key={trial.ID} value={trial.ID}>
                            {trial.TrialCode} - {trial.Product_ProductToTrial?.ProductName || "-"}
                            {trial.Purpose && ` - ${trial.Purpose}`}
                          </option>
                        ))}
                      </select>
                      {selectedDayIdx === dayIdx && selectedTrialId && (
                        <>
                          <input
                            type="number"
                            className="input-classic"
                            style={{ width: "80px" }}
                            value={plannedOrder}
                            onChange={(e) => setPlannedOrder(parseInt(e.target.value) || 1)}
                            min={1}
                            placeholder="#"
                          />
                          <button
                            className="btn-primary"
                            onClick={handleAddToSchedule}
                          >
                            ➕ {t("Dodaj")}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <ToastContainer
        autoClose={5000}
        position="bottom-right"
        hideProgressBar={false}
        theme="colored"
      />
    </div>
  );
};

export default TrialDashboard;
